/**
 * Générateur — Effectuer une boucle inconditionnelle simple permettant de
 * répéter une séquence linéaire d'instructions un nombre précis de fois.
 * Attendu ID : 1002 | Niveau : 5ème | Domaine : Pensée informatique (cluster 5, O7)
 *
 * Source : doc prof « TANOED EXERCISEUR la pensée informatique », cluster 5
 * (Ex.1, Ex.2, Ex.3 question d, Créé n°63).
 *
 * Plan de cartes (validé 2026-07-12) :
 *
 *   Carte « boucle_carre » — Ex.1a + Ex.3d (FUSION actée au plan : même
 *   geste, seule la taille change) : le script déroulé du carré (4 ×
 *   [avancer, tourner]) est affiché → l'écrire avec « répéter ».
 *   texte_trou_saisie (répéter {{4}} fois, avancer {{c}}, tourner {{90}}) +
 *   qcm (le script en boucle équivalent — distracteurs : 3 répétitions,
 *   angle = côté, octogone à 45°).
 *
 *   Carte « enchainer » — Ex.1 b + c : enchaîner k carrés. qcm (b : carrés
 *   collés — le distracteur « répéter k fois [avancer puis carré] » est
 *   ÉCARTÉ car il tracerait AUSSI k carrés collés : deux bonnes réponses) +
 *   qcm (c : avec un espace — LE piège du doc : oublier de relever le
 *   stylo ; et l'espace e sans la largeur du carré) + texte_trou_saisie
 *   (distance entre départs = c + e par construction).
 *
 *   Carte « polygone » — Ex.2 : répéter n fois [avancer d, tourner 360/n°].
 *   qcm à choix IMAGES (quelle figure ? — 3 polygones réguliers distincts) +
 *   texte_trou_drag (compléter n et l'angle pour le polygone dessiné — le
 *   distracteur d'angle est l'ANGLE INTÉRIEUR 180 − 360/n, le piège
 *   fondamental du doc ; pour le carré ils coïncident, filtrerDistracteurs
 *   l'écarte automatiquement).
 *
 *   Carte « motif » — Créé n°63, l'exemple exact du cadrage O7 : séquence à
 *   la main vs « répéter 10 fois ». qcm (a : que dessine le motif — escalier
 *   montant/descendant selon le sens des rotations, par construction) + qcm
 *   (b : même figure ?) + texte_trou_saisie (c : 4 × reps blocs déroulés —
 *   l'économie quantifiée) + qcm (d : l'avantage de la boucle).
 */

import {
    randomChoice, randInt, makeQcm, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';
import { imgElement, imgChoix, svgScript, svgScripts, svgTraces } from './svgScratch';

const ATTENDU_ID = 1002;

const L_BOUCLE_CARRE = 'Écrire le carré avec une boucle';
const L_ENCHAINER = 'Enchaîner les carrés';
const L_POLYGONE = "Prédire la figure d'une boucle";
const L_MOTIF = 'De la séquence à la boucle';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ Carte 1 — Écrire le carré avec une boucle (Ex.1a + Ex.3d) ═════════

const tirerCote = () => randInt(4, 30) * 10;

const svgCarreDeroule = (c) => {
    const blocs = [
        { t: 'hat', txt: 'quand le drapeau est cliqué' },
        { t: 'pen', txt: "stylo en position d'écriture" },
    ];
    for (let i = 0; i < 4; i++) {
        blocs.push({ t: 'move', txt: `avancer de ${c} pas` });
        blocs.push({ t: 'move', txt: 'tourner ↻ de 90 degrés' });
    }
    return svgScript(blocs);
};

const genBoucleCarreTrous = () => {
    const c = tirerCote();
    const phrase = `répéter {{4}} fois : avancer de {{${c}}} pas, tourner ↻ de {{90}} degrés.`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Ce script trace un carré en écrivant chaque instruction à la main. '
        + 'Réécris-le avec le moins d\'instructions possible grâce au bloc '
        + '« répéter … fois ».</b></p>',
        { ...opts(L_BOUCLE_CARRE), mode: 'saisie', elements_additionnels: imgElement(svgCarreDeroule(c)) });
    payload._meta.params = { c };
    return payload;
};

const genBoucleCarreQcm = () => {
    let c = tirerCote();
    if (c === 90) c = 100;   // sinon « tourner de c degrés » = la bonne réponse (débusqué au harnais)
    const texte = '<p><b>Quel programme avec une boucle trace exactement le même '
        + 'carré que ce script ?</b></p>';
    // ↻ sur TOUS les choix (2026-07-28) : ils citent des scripts, et le script
    // affiché (svgCarreDeroule) montre « tourner ↻ » — fidélité + pas d'indice.
    const correct = `répéter 4 fois : [ avancer de ${c} pas — tourner ↻ de 90 degrés ]`;
    const candidats = [
        `répéter 3 fois : [ avancer de ${c} pas — tourner ↻ de 90 degrés ]`,   // dernier côté oublié
        `répéter 4 fois : [ avancer de ${c} pas — tourner ↻ de ${c} degrés ]`, // angle confondu avec le côté
        `répéter 8 fois : [ avancer de ${c} pas — tourner ↻ de 45 degrés ]`,   // octogone
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte,
        { ...opts(L_BOUCLE_CARRE), elements_additionnels: imgElement(svgCarreDeroule(c)) });
    payload._meta.params = { c };
    return payload;
};

// ═════════ Carte 2 — Enchaîner les carrés (Ex.1 b et c) ═════════

const svgBoucleCarre = (c) => svgScript([
    { t: 'hat', txt: 'quand le drapeau est cliqué' },
    { t: 'pen', txt: "stylo en position d'écriture" },
    {
        t: 'repeat', n: 4,
        body: [
            { t: 'move', txt: `avancer de ${c} pas` },
            { t: 'move', txt: 'tourner ↻ de 90 degrés' },
        ],
    },
]);

const tirerEnchainement = () => ({ c: randInt(3, 12) * 10, k: randInt(2, 4) });

const genEnchainerQcm = () => {
    const { c, k } = tirerEnchainement();
    const texte = `<p><b>La « Boucle du carré » ci-dessous trace un carré de ${c} pas `
        + `de côté. Quel programme trace ${k} carrés collés, côte à côte ?</b></p>`;
    const correct = `répéter ${k} fois : [ Boucle du carré — avancer de ${c} pas ]`;
    const candidats = [
        `répéter ${k} fois : [ Boucle du carré ]`,                       // les carrés se superposent
        `Boucle du carré, puis répéter ${k} fois : [ avancer de ${c} pas ]`, // un seul carré
        `répéter ${k} fois : [ Boucle du carré — tourner ↻ de 90 degrés ]`,    // les carrés pivotent sur place
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte,
        { ...opts(L_ENCHAINER), elements_additionnels: imgElement(svgBoucleCarre(c)) });
    payload._meta.params = { c, k };
    return payload;
};

const genEnchainerStyloQcm = () => {
    const { c, k } = tirerEnchainement();
    const e = randInt(2, 8) * 10;
    const texte = `<p><b>On trace ${k} carrés de ${c} pas de côté, en laissant un `
        + `espace de ${e} pas entre deux carrés voisins. Après chaque carré, que doit `
        + 'faire le lutin avant de tracer le suivant ?</b></p>';
    const correct = `Relever le stylo, avancer de ${c + e} pas, reposer le stylo`;
    const candidats = [
        `Avancer de ${c + e} pas sans toucher au stylo`,          // LE piège : un trait entre les carrés
        `Relever le stylo, avancer de ${e} pas, reposer le stylo`, // l'espace sans la largeur du carré
        'Relever le stylo, tourner ↻ de 90 degrés, reposer le stylo',
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte,
        { ...opts(L_ENCHAINER), elements_additionnels: imgElement(svgBoucleCarre(c)) });
    payload._meta.params = { c, k, e };
    return payload;
};

const genEnchainerTrous = () => {
    const { c, k } = tirerEnchainement();
    const e = randInt(2, 8) * 10;
    const phrase = `On répète {{${k}}} fois la séquence « Boucle du carré, puis déplacement ». `
        + `Entre les départs de deux carrés voisins, le lutin avance de {{${c + e}}} pas, stylo relevé.`;
    const payload = makeTexteTrou(phrase,
        `<p><b>On veut ${k} carrés de ${c} pas de côté, espacés de ${e} pas. `
        + 'Complète.</b></p>',
        { ...opts(L_ENCHAINER), mode: 'saisie', elements_additionnels: imgElement(svgBoucleCarre(c)) });
    payload._meta.params = { c, k, e };
    return payload;
};

// ═════════ Carte 3 — Prédire la figure d'une boucle (Ex.2) ═════════

const NOMS_POLYGONES = { 3: 'triangle', 4: 'carré', 5: 'pentagone', 6: 'hexagone', 8: 'octogone' };
const nomPolygone = (n) => ([6, 8].includes(n) ? `l'${NOMS_POLYGONES[n]}` : `le ${NOMS_POLYGONES[n]}`);
const POLYGONES = [3, 4, 5, 6, 8];

/** Sommets d'un polygone régulier tracé par [avancer d, tourner ↻ 360/n°]. */
const pointsPolygone = (n, d) => {
    const pts = [[0, 0]];
    let angle = 0;   // orientation initiale : vers la droite
    let x = 0, y = 0;
    for (let i = 0; i < n; i++) {
        x += d * Math.cos(angle);
        y += d * Math.sin(angle);
        pts.push([Math.round(x * 100) / 100, Math.round(y * 100) / 100]);
        angle -= (2 * Math.PI) / n;   // ↻ : sens horaire
    }
    return pts;
};

const svgPolygone = (n, d, compact) =>
    svgTraces([{ points: pointsPolygone(n, d), couleur: '#1E3A8A' }], { compact });

const genPolygoneQcm = () => {
    const n = randomChoice(POLYGONES);
    const d = randInt(3, 8) * 10;
    // deux polygones distracteurs distincts
    const alt = POLYGONES.filter((m) => m !== n);
    const i1 = randInt(0, alt.length - 1);
    const m1p = alt.splice(i1, 1)[0];
    const m2p = alt[randInt(0, alt.length - 1)];
    const texte = `<p><b>Le lutin exécute : répéter ${n} fois `
        + `[ avancer de ${d} pas — tourner ↻ de ${360 / n} degrés ]. `
        + 'Quelle figure trace-t-il ?</b></p>';
    const choixCorrect = imgChoix(svgPolygone(n, d, true));
    const choix = [choixCorrect, imgChoix(svgPolygone(m1p, d, true)), imgChoix(svgPolygone(m2p, d, true))];
    const payload = makeQcm(choix, choixCorrect, texte, opts(L_POLYGONE));
    payload._meta.params = { n, d };
    return payload;
};

const genPolygoneDrag = () => {
    const n = randomChoice(POLYGONES);
    const d = randInt(3, 8) * 10;
    const angle = 360 / n;
    const phrase = `répéter {{${n}}} fois : avancer de ${d} pas, tourner ↻ de {{${angle}}} degrés.`;
    const distracteurs = filtrerDistracteurs(phrase, [
        String(180 - angle),          // l'angle INTÉRIEUR : le piège fondamental
        String(n + 1),
        String(n === 3 ? 4 : n - 1),
        '360',
    ]).slice(0, 3);
    const payload = makeTexteTrou(phrase,
        `<p><b>Complète le script pour que le lutin trace ${nomPolygone(n)} `
        + 'régulier ci-dessous.</b></p>'
        + '<p>Attention : le lutin tourne de l\'angle EXTÉRIEUR, pas de l\'angle de '
        + 'la figure.</p>',
        {
            ...opts(L_POLYGONE), mode: 'drag', distracteurs,
            elements_additionnels: imgElement(svgPolygone(n, d, false)),
        });
    payload._meta.params = { n, d, angle };
    return payload;
};

// ═════════ Carte 4 — De la séquence à la boucle (Créé n°63) ═════════

const tirerMotif = () => ({
    reps: randInt(5, 12),
    a: randomChoice([10, 20, 30]),
    monte: randomChoice([true, false]),   // ↺ d'abord : escalier qui monte
});

const blocsMotif = (t) => {
    const [r1, r2] = t.monte ? ['↺', '↻'] : ['↻', '↺'];
    return [
        { t: 'move', txt: `avancer de ${t.a} pas` },
        { t: 'move', txt: `tourner ${r1} de 90 degrés` },
        { t: 'move', txt: `avancer de ${t.a} pas` },
        { t: 'move', txt: `tourner ${r2} de 90 degrés` },
    ];
};

const svgMotif = (t) => svgScripts([
    {
        titre: 'Programme de gauche',
        blocs: [
            { t: 'hat', txt: 'quand le drapeau est cliqué' },
            { t: 'pen', txt: "stylo en position d'écriture" },
            ...blocsMotif(t), ...blocsMotif(t),
            { t: 'control', txt: `… la séquence est écrite ${t.reps} fois` },
        ],
    },
    {
        titre: 'Programme de droite',
        blocs: [
            { t: 'hat', txt: 'quand le drapeau est cliqué' },
            { t: 'pen', txt: "stylo en position d'écriture" },
            { t: 'repeat', n: t.reps, body: blocsMotif(t) },
        ],
    },
]);

const genMotifQcm = () => {
    const t = tirerMotif();
    const sens = t.monte ? 'monte' : 'descend';
    const texte = '<p><b>Le lutin part orienté vers la droite. Que dessine le motif '
        + 'répété par ces programmes ?</b></p>';
    const correct = `Un escalier qui ${sens} vers la droite.`;
    const candidats = [
        `Un escalier qui ${t.monte ? 'descend' : 'monte'} vers la droite.`,  // sens des rotations inversé
        `Un carré de ${t.a} pas de côté.`,                                    // les rotations lues identiques
        'Une ligne droite horizontale.',                                      // les rotations ignorées
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte,
        { ...opts(L_MOTIF), elements_additionnels: imgElement(svgMotif(t)) });
    payload._meta.params = t;
    return payload;
};

const genMotifMemeQcm = () => {
    const t = tirerMotif();
    const texte = '<p><b>Les deux programmes tracent-ils la même figure ?</b></p>';
    const correct = `Oui : le programme de droite répète ${t.reps} fois exactement la même séquence.`;
    const candidats = [
        `Oui, mais la figure de droite est ${t.reps} fois plus grande.`,   // boucle lue « agrandissement »
        'Non : le bloc « répéter » change la forme du tracé.',
        'Non : le programme de gauche trace un escalier plus long.',
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte,
        { ...opts(L_MOTIF), elements_additionnels: imgElement(svgMotif(t)) });
    payload._meta.params = t;
    return payload;
};

const genMotifTrous = () => {
    const t = tirerMotif();
    const phrase = `La séquence du motif contient {{4}} blocs. Pour égaler les `
        + `${t.reps} répétitions du programme de droite, le programme de gauche doit `
        + `contenir {{${4 * t.reps}}} blocs de déplacement.`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Compte les blocs : c\'est toute l\'économie de la boucle.</b></p>',
        { ...opts(L_MOTIF), mode: 'saisie', elements_additionnels: imgElement(svgMotif(t)) });
    payload._meta.params = t;
    return payload;
};

const genMotifAvantageQcm = () => {
    const t = tirerMotif();
    const texte = '<p><b>Quel est l\'avantage du programme de droite (avec la '
        + 'boucle) ?</b></p>';
    const correct = 'Il est plus court à écrire et plus facile à modifier, pour un tracé identique.';
    const candidats = [
        'Pendant l\'exécution, le lutin exécute moins de blocs « avancer ».',  // faux : autant à l'exécution
        'Il trace une figure plus grande.',
        'Sa figure est plus précise que celle de gauche.',
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte,
        { ...opts(L_MOTIF), elements_additionnels: imgElement(svgMotif(t)) });
    payload._meta.params = t;
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    boucle_carre_texte_trou_saisie: genBoucleCarreTrous,
    boucle_carre_qcm_unique: genBoucleCarreQcm,
    enchainer_qcm_unique: genEnchainerQcm,
    enchainer_stylo_qcm_unique: genEnchainerStyloQcm,
    enchainer_texte_trou_saisie: genEnchainerTrous,
    polygone_qcm_unique: genPolygoneQcm,
    polygone_texte_trou_drag: genPolygoneDrag,
    motif_qcm_unique: genMotifQcm,
    motif_meme_qcm_unique: genMotifMemeQcm,
    motif_texte_trou_saisie: genMotifTrous,
    motif_avantage_qcm_unique: genMotifAvantageQcm,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag',
    qcm_unique: 'QCM', stylo_qcm_unique: 'QCM stylo',
    meme_qcm_unique: 'QCM comparaison', avantage_qcm_unique: 'QCM avantage',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'boucle_carre', label: L_BOUCLE_CARRE, formatsList: buildFormats('boucle_carre') },
    { id: 'enchainer', label: L_ENCHAINER, formatsList: buildFormats('enchainer') },
    { id: 'polygone', label: L_POLYGONE, formatsList: buildFormats('polygone') },
    { id: 'motif', label: L_MOTIF, formatsList: buildFormats('motif') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
